import React, { useEffect, useState, useCallback } from "react";
import { Download, Search, FileSpreadsheet } from "lucide-react";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { getExamsAdmin } from "../../services/ExamService";
import {
  getExamResults,
  getAllExamResults,
} from "../../services/ExamResultService";

export default function ExamResults() {
  const [exams, setExams] = useState([]);
  const [selectedExamId, setSelectedExamId] = useState("");
  const [selectedExam, setSelectedExam] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingExams, setLoadingExams] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const limit = 10;

  // Fetch danh sách bài thi
  useEffect(() => {
    const fetchExams = async () => {
      try {
        setLoadingExams(true);
        const response = await getExamsAdmin(1, 1000, ""); // Lấy tất cả bài thi
        setExams(response?.data || []);
      } catch (error) {
        const message =
          error?.response?.data?.message || "Lỗi khi tải danh sách bài thi";
        toast.error(message);
      } finally {
        setLoadingExams(false);
      }
    };
    fetchExams();
  }, []);

  const fetchResults = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getExamResults(selectedExamId, currentPage, limit);

      // Tìm thông tin bài thi đã chọn
      const exam = exams.find((e) => e._id === selectedExamId);
      setSelectedExam(exam);

      // Xử lý dữ liệu kết quả
      // API trả về { data: [...], pagination: {...} }
      let data = [];
      let pagination = {};

      if (response?.data?.data && Array.isArray(response.data.data)) {
        data = response.data.data;
        pagination = response.data.pagination || {};
      } else if (response?.data && Array.isArray(response.data)) {
        data = response.data;
        pagination = response.pagination || {};
      } else if (response?.data && response?.pagination) {
        data = response.data;
        pagination = response.pagination;
      }

      setResults(data);
      setTotalPages(pagination.totalPages || 1);
      setTotalResults(pagination.totalResults || data.length);
    } catch (error) {
      const message =
        error?.response?.data?.message || "Lỗi khi tải kết quả bài thi";
      toast.error(message);
      setResults([]);
      setTotalPages(1);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  }, [selectedExamId, currentPage, limit, exams]);

  // Fetch kết quả khi chọn bài thi hoặc đổi trang
  useEffect(() => {
    if (selectedExamId) {
      fetchResults();
    } else {
      setResults([]);
      setSelectedExam(null);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalResults(0);
    }
  }, [selectedExamId, currentPage, fetchResults]);

  const handleExportExcel = async () => {
    if (!selectedExamId) {
      toast.warning("Vui lòng chọn bài thi để xuất");
      return;
    }

    try {
      toast.info("Đang tải tất cả dữ liệu để xuất Excel...");

      // Lấy tất cả kết quả từ DB (gọi với limit rất lớn)
      const response = await getAllExamResults(selectedExamId);

      // API trả về { data: [...], pagination: {...} }
      let allResults = [];
      if (response?.data?.data && Array.isArray(response.data.data)) {
        allResults = response.data.data;
      } else if (response?.data && Array.isArray(response.data)) {
        allResults = response.data;
      }

      // Nếu vẫn còn dữ liệu, gọi thêm các trang tiếp theo
      const pagination =
        response?.data?.pagination || response?.pagination || {};
      const totalPages = pagination.totalPages || 1;

      if (totalPages > 1) {
        // Gọi thêm các trang còn lại
        const additionalPages = [];
        for (let page = 2; page <= totalPages; page++) {
          try {
            const pageResponse = await getExamResults(
              selectedExamId,
              page,
              10000
            );
            if (
              pageResponse?.data?.data &&
              Array.isArray(pageResponse.data.data)
            ) {
              additionalPages.push(...pageResponse.data.data);
            } else if (pageResponse?.data && Array.isArray(pageResponse.data)) {
              additionalPages.push(...pageResponse.data);
            }
          } catch (err) {
            console.error(`Error fetching page ${page}:`, err);
          }
        }
        allResults = [...allResults, ...additionalPages];
      }

      if (allResults.length === 0) {
        toast.warning("Không có dữ liệu để xuất");
        return;
      }

      // Chuẩn bị dữ liệu cho Excel
      const excelData = allResults.map((result, index) => {
        const user = result.userId;
        const userName =
          typeof user === "object" ? user?.fullName || "N/A" : "N/A";

        return {
          STT: index + 1,
          "Họ và tên": userName,
          Email: result.userId?.email || "N/A",
          "Số báo danh":
            result.userId?.identifier || result.userId?._id || "N/A",
          "Điểm số": result.totalScore || 0,
          "Điểm tối đa": result.maxOverallScore || 0,
          "Tỷ lệ (%)": result.maxOverallScore
            ? ((result.totalScore / result.maxOverallScore) * 100).toFixed(2) +
              "%"
            : "0%",
          "Thời gian bắt đầu": result.createdAt
            ? dayjs(result.createdAt).format("DD/MM/YYYY HH:mm:ss")
            : "N/A",
          "Thời gian nộp bài": result.updatedAt
            ? dayjs(result.updatedAt).format("DD/MM/YYYY HH:mm:ss")
            : "N/A",
        };
      });

      const headers = [
        "STT",
        "Họ và tên",
        "Email",
        "Số báo danh",
        "Điểm số",
        "Điểm tối đa",
        "Tỷ lệ (%)",
        "Thời gian bắt đầu",
        "Thời gian nộp bài",
      ];

      // Tạo workbook và worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData, { header: headers });
      // đảm bảo header nằm ở dòng 1
      XLSX.utils.sheet_add_aoa(ws, [headers], { origin: "A1" });

      // Đặt độ rộng cột
      const colWidths = [
        { wch: 5 }, // STT
        { wch: 25 }, // Họ và tên
        { wch: 25 }, // Email
        { wch: 20 }, // Số báo danh
        { wch: 10 }, // Điểm số
        { wch: 12 }, // Điểm tối đa
        { wch: 12 }, // Tỷ lệ
        { wch: 20 }, // Thời gian bắt đầu
        { wch: 20 }, // Thời gian nộp bài
      ];
      ws["!cols"] = colWidths;

      // Thêm worksheet vào workbook
      XLSX.utils.book_append_sheet(wb, ws, "Kết quả bài thi");

      // Tạo tên file
      const examTitle = selectedExam?.title || "KetQuaBaiThi";
      const fileName = `${examTitle}_${dayjs().format(
        "YYYY-MM-DD_HH-mm-ss"
      )}.xlsx`;

      // Xuất file
      XLSX.writeFile(wb, fileName);
      toast.success(`Đã xuất file Excel: ${fileName}`);
    } catch (error) {
      console.error("Error exporting Excel:", error);
      toast.error("Lỗi khi xuất file Excel: " + error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 overflow-y-auto pb-20">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <FileSpreadsheet className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-4xl font-bold mb-2">Kết Quả Bài Thi</h1>
                <p className="text-blue-100 text-lg">
                  Xem và xuất kết quả bài thi của học sinh
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Select Bài Thi */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20 mb-8">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="flex-1 w-full">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Chọn bài thi
              </label>
              <select
                value={selectedExamId}
                onChange={(e) => setSelectedExamId(e.target.value)}
                disabled={loadingExams}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">-- Chọn bài thi --</option>
                {exams.map((exam) => (
                  <option key={exam._id} value={exam._id}>
                    {exam.title} ({exam.type})
                  </option>
                ))}
              </select>
            </div>
            {selectedExamId && (
              <button
                onClick={handleExportExcel}
                disabled={loading || totalResults === 0}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all duration-200 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                <Download className="w-5 h-5" />
                <span>Xuất Excel ({totalResults || 0} kết quả)</span>
              </button>
            )}
          </div>
        </div>

        {/* Thông tin bài thi */}
        {selectedExam && (
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {selectedExam.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <span className="text-sm text-gray-600">Loại:</span>
                <span className="ml-2 font-semibold text-gray-900">
                  {selectedExam.type}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-600">Môn học:</span>
                <span className="ml-2 font-semibold text-gray-900">
                  {selectedExam.subject}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-600">Số câu hỏi:</span>
                <span className="ml-2 font-semibold text-gray-900">
                  {selectedExam.numberOfQuestions}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Table Kết Quả */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Đang tải dữ liệu...</p>
            </div>
          ) : results.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        STT
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Họ và tên
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Số báo danh
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Điểm số
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Điểm tối đa
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Tỷ lệ (%)
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Ngày thi
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {results.map((result, index) => {
                      const user = result.userId;
                      const userName =
                        typeof user === "object"
                          ? user?.fullName || "N/A"
                          : "N/A";
                      const userId =
                        typeof user === "object"
                          ? user?.identifier || user?._id || "N/A"
                          : "N/A";
                      const percentage = result.maxOverallScore
                        ? (
                            (result.totalScore / result.maxOverallScore) *
                            100
                          ).toFixed(2)
                        : "0";

                      return (
                        <tr
                          key={result._id}
                          className="hover:bg-gray-50 transition-colors duration-200"
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {(currentPage - 1) * limit + index + 1}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {userName}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {userId}
                          </td>
                          <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                            {result.totalScore || 0}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {result.maxOverallScore || 0}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                parseFloat(percentage) >= 80
                                  ? "bg-green-100 text-green-800"
                                  : parseFloat(percentage) >= 60
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {percentage}%
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {result.createdAt
                              ? dayjs(result.createdAt).format(
                                  "DD/MM/YYYY HH:mm"
                                )
                              : "N/A"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">
                    Hiển thị {results.length} / <strong>{totalResults}</strong>{" "}
                    kết quả
                  </span>
                  {totalPages > 1 && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() =>
                          setCurrentPage((p) => Math.max(p - 1, 1))
                        }
                        disabled={currentPage === 1 || loading}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        Trước
                      </button>
                      <span className="px-4 py-2 text-sm text-gray-700">
                        Trang {currentPage} / {totalPages}
                      </span>
                      <button
                        onClick={() =>
                          setCurrentPage((p) => (p < totalPages ? p + 1 : p))
                        }
                        disabled={currentPage === totalPages || loading}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        Sau
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : selectedExamId ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Không có kết quả nào
              </h3>
              <p className="text-gray-500">
                Chưa có học sinh nào làm bài thi này
              </p>
            </div>
          ) : (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileSpreadsheet className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Chọn bài thi để xem kết quả
              </h3>
              <p className="text-gray-500">
                Vui lòng chọn bài thi từ dropdown phía trên
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
